/**
 * AnalysisHistoryPage
 * Full paginated list of all user's deep analysis sessions
 * Supports compare mode: select two completed analyses for side-by-side comparison
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, DeepAnalysisHistoryItem } from '../services/api';
import { SessionCard } from '../components/deep-analysis';
import { getStatusLabel, getStatusBadgeClasses } from '../components/deep-analysis/utils/sessionMapper';
import { useTranslation } from 'react-i18next';

type StatusFilter = 'all' | 'complete' | 'error' | 'in_progress';

export default function AnalysisHistoryPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<DeepAnalysisHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';

  // Pagination
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 12;

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);

  const fetchSessions = useCallback(async (reset = false) => {
    try {
      setIsLoading(true);
      const currentOffset = reset ? 0 : offset;
      const response = await api.getDeepAnalysisHistory({ limit, offset: currentOffset });

      // Map the response
      const mappedSessions: DeepAnalysisHistoryItem[] = response.sessions.map(s => ({
        id: s.id,
        status: s.status,
        phase: s.phase,
        mode: s.mode,
        documentPreview: (s as any).documentPreview || '',
        tokensTotal: (s as any).tokensTotal || 0,
        createdAt: s.createdAt,
        completedAt: s.completedAt,
        durationMs: s.durationMs,
        hasReport: s.hasReport,
        isShared: s.isShared,
      }));

      if (reset) {
        setSessions(mappedSessions);
        setOffset(limit);
      } else {
        setSessions(prev => [...prev, ...mappedSessions]);
        setOffset(prev => prev + limit);
      }

      setHasMore(mappedSessions.length === limit);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('analysisHistory.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [offset, t]);

  useEffect(() => {
    fetchSessions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSessionSelect = useCallback((sessionId: string) => {
    setLoadingSessionId(sessionId);
    // Navigate to analysis page with session ID to load
    navigate(`/analysis?session=${sessionId}`);
  }, [navigate]);

  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchSessions(false);
    }
  }, [isLoading, hasMore, fetchSessions]);

  const handleCompareToggle = useCallback((sessionId: string) => {
    setSelectedForCompare(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      }
      if (prev.length >= 2) return prev;
      return [...prev, sessionId];
    });
  }, []);

  const handleCompareNavigate = useCallback(() => {
    if (selectedForCompare.length === 2) {
      navigate(`/analysis/compare?session1=${selectedForCompare[0]}&session2=${selectedForCompare[1]}`);
    }
  }, [selectedForCompare, navigate]);

  const exitCompareMode = useCallback(() => {
    setCompareMode(false);
    setSelectedForCompare([]);
  }, []);

  // Filter sessions client-side
  const filteredSessions = sessions.filter(session => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'complete') return session.status === 'complete';
    if (statusFilter === 'error') return session.status === 'error';
    if (statusFilter === 'in_progress') {
      return ['pending', 'clarification', 'exploring', 'analyzing', 'synthesizing'].includes(session.status);
    }
    return true;
  });

  // Format date for list view
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format tokens
  const formatTokens = (tokens: number) => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return String(tokens);
  };

  const isCompareEligible = (session: DeepAnalysisHistoryItem) => session.status === 'complete';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {t('analysisHistory.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('analysisHistory.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {compareMode ? (
            <>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {t('analysisComparison.selected', { count: selectedForCompare.length })}
              </span>
              <button
                onClick={handleCompareNavigate}
                disabled={selectedForCompare.length !== 2}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('analysisComparison.compareButton')}
              </button>
              <button
                onClick={exitCompareMode}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('analysisComparison.cancelCompare')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setCompareMode(true)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('analysisComparison.compareMode')}
              </button>
              <Link
                to="/analysis"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('analysisHistory.newAnalysis')}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Compare mode hint */}
      {compareMode && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-purple-700 dark:text-purple-300">
            {t('analysisComparison.selectTwo')}
            {' '}
            <span className="text-purple-500 dark:text-purple-400 text-xs">
              ({t('analysisComparison.onlyComplete')})
            </span>
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">{t('analysisHistory.status')}:</span>
          <div className="flex gap-1">
            {[
              { value: 'all', label: t('analysisHistory.filterAll') },
              { value: 'complete', label: t('analysisHistory.filterComplete') },
              { value: 'in_progress', label: t('analysisHistory.filterInProgress') },
              { value: 'error', label: t('analysisHistory.filterErrors') },
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value as StatusFilter)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sessions list */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        {isLoading && sessions.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <svg className="w-8 h-8 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => fetchSessions(true)}
              className="mt-4 px-4 py-2 text-sm text-purple-600 dark:text-purple-400 hover:underline"
            >
              {t('analysisHistory.tryAgain')}
            </button>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="py-16 text-center">
            <svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-slate-500 dark:text-slate-400">
              {statusFilter === 'all' ? t('analysisHistory.noHistory') : t('analysisHistory.noFilteredResults')}
            </p>
            <Link
              to="/analysis"
              className="mt-4 inline-block px-4 py-2 text-sm text-purple-600 dark:text-purple-400 hover:underline"
            >
              {t('analysisHistory.startFirst')}
            </Link>
          </div>
        ) : (
          <>
            {/* Table view for desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900/50">
                  <tr>
                    {compareMode && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-12">
                        &nbsp;
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t('analysisHistory.date')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t('analysisHistory.document')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t('analysisHistory.statusColumn')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t('analysisHistory.tokensColumn')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {t('analysisHistory.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredSessions.map(session => {
                    const eligible = isCompareEligible(session);
                    const isSelected = selectedForCompare.includes(session.id);
                    const canSelect = eligible && (isSelected || selectedForCompare.length < 2);

                    return (
                      <tr
                        key={session.id}
                        className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                          compareMode && isSelected ? 'bg-purple-50 dark:bg-purple-900/10' : ''
                        }`}
                      >
                        {compareMode && (
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!canSelect}
                              onChange={() => handleCompareToggle(session.id)}
                              className="w-4 h-4 text-purple-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-purple-500 disabled:opacity-40"
                              aria-label={`Select ${session.id} for comparison`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                          {formatDate(session.createdAt)}
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-2 max-w-md">
                            {session.documentPreview}
                          </p>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeClasses(session.status as 'complete' | 'error' | 'clarification' | 'pending')}`}>
                              {t(getStatusLabel(session.status as 'complete' | 'error' | 'clarification' | 'pending'))}
                            </span>
                            {session.isShared && (
                              <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                                {t('analysisHistory.shared')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                          {formatTokens(session.tokensTotal)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          {!compareMode && (
                            <button
                              onClick={() => handleSessionSelect(session.id)}
                              disabled={loadingSessionId === session.id}
                              className="px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {loadingSessionId === session.id ? t('analysisHistory.loading') : t('analysisHistory.open')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Card view for mobile */}
            <div className="md:hidden p-4 grid grid-cols-1 gap-3">
              {filteredSessions.map(session => {
                if (compareMode) {
                  const eligible = isCompareEligible(session);
                  const isSelected = selectedForCompare.includes(session.id);
                  const canSelect = eligible && (isSelected || selectedForCompare.length < 2);

                  return (
                    <div key={session.id} className="relative">
                      {eligible && (
                        <div className="absolute top-3 right-3 z-10">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!canSelect}
                            onChange={() => handleCompareToggle(session.id)}
                            className="w-5 h-5 text-purple-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-purple-500 disabled:opacity-40"
                            aria-label={`Select ${session.id} for comparison`}
                          />
                        </div>
                      )}
                      <SessionCard
                        session={session}
                        onClick={() => eligible ? handleCompareToggle(session.id) : undefined}
                        isLoading={false}
                      />
                    </div>
                  );
                }

                return (
                  <SessionCard
                    key={session.id}
                    session={session}
                    onClick={handleSessionSelect}
                    isLoading={loadingSessionId === session.id}
                  />
                );
              })}
            </div>

            {/* Load more button */}
            {hasMore && (
              <div className="p-4 text-center border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="px-6 py-2 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t('analysisHistory.loadingMore')}
                    </span>
                  ) : (
                    t('analysisHistory.loadMore')
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
